/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo } from 'react';
import {
	Button,
	Dialog,
	DialogTitle,
	DialogActions,
	DialogContent,
	CircularProgress,
	FormHelperText,
	Skeleton,
	Stack,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { showToast } from '@/features/toast/toastSlice';
import { useCreateContactMutation, useListContactsQuery } from '@/features/contacts/contactsApi';
import validationSchema from './validationSchema';
import { FormikErrorScroll } from '@/hooks/useFormikHelper';
import {
	CONTACT_ERROR,
	EMAIL,
	EXISTING_CONTACT,
	FIRST_NAME,
	IS_MAIN_CONTACT,
	LAST_NAME,
	PHONE,
	ROLE,
	PHONE2,
	DATE_OF_BIRTH,
} from '@/components/forms/CreateNewBusinessForm/validationSchema';
import { useObjectContext } from '@/hooks/useObjectContext';
import ClientSchemaProvider, { useClientSchemaContext } from '@/context/clientSchemaContext';
import { isProspectFieldVisible, VIEW_TYPES } from '@/features/config/helper';
import { useParams } from 'react-router-dom';
import { useGetFormConfigQuery } from '@/features/config/configApi';
import DevJSONView from '@/components/DevJSONView';
import { ContactField } from '@/components/forms/CreateClientForm/Fields/ContactFields';
import { OldContactFields } from '@/components/forms/CreateNewBusinessForm/Fields/OldContactFields';
import { useFeatureFlagContext } from '@/context/featureFlagContext';

const GROUP = 'contact';

const AddContactFormWrapper = ({ onClose }) => {
	const params = useParams();
	const dispatch = useDispatch();
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const [createContact, createContactState] = useCreateContactMutation();

	const {
		isProspect,
		view,
		initialValues,
		schema: validationSchema,
		isLoading,
	} = useClientSchemaContext();

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const fieldsQuery = useGetFormConfigQuery(
		{ configType: 'client' },
		{
			skip: !userDetails,
			selectFromResult: ({ data, ...rest }) => ({
				...rest,
				data:
					data?.filter(
						(f) =>
							f.group === GROUP &&
							(isProspect ? isProspectFieldVisible(f, view) : f.visible?.[view]),
					) ?? [],
			}),
		},
	);

	const fields = useMemo(() => {
		const data = (fieldsQuery.data ?? []).slice().map((f) => ({
			...f,
			required: f.mandatory?.[isProspect ? 'prospect' : 'client'],
		}));
		return data.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
	}, [fieldsQuery.data, isProspect]);

	const { data: contacts, ...contactQuery } = useListContactsQuery(params.clientId, {
		skip: !params.clientId,
		selectFromResult: ({ data, ...rest }) => {
			const contacts = data ?? [];
			return {
				...rest,
				data: contacts,
			};
		},
	});

	useEffect(() => console.log('Client contacts', contacts), [contacts]);
	const hasExistingContact = contacts.length > 0;

	return (
		<Formik
			{...{
				initialValues,
				validationSchema,
				validateOnBlur: true,
				enableReinitialize: true,
				onSubmit: async (values, { resetForm }) => {
					console.log('[onSubmit] Submitting values', values);
					const { contacts } = values;
					const newContact = contacts[0];

					try {
						await createContact({
							clientId: params.clientId,
							...newContact,
						}).unwrap();

						dispatch(
							showToast({
								message: 'Created new contact!',
								success: true,
								autohide: true,
							}),
						);
						resetForm();
						onClose?.();
					} catch (e) {
						console.error('[onSubmit] Error adding contact', e);
						const message = e.data?.message ?? e?.message;
						dispatch(
							showToast({
								message: `Error adding contact: ${message}`,
								error: true,
							}),
						);
					}
				},
			}}
		>
			{({ isSubmitting, values, handleSubmit, errors, validateForm, resetForm }) => {
				const disabled = isSubmitting || createContactState.isLoading;

				return (
					<Form>
						<FormikErrorScroll {...{ isSubmitting }} />
						<DialogTitle>Add Contact</DialogTitle>
						<DialogContent sx={{ paddingBottom: '2em' }}>
							{isLoading || fieldsQuery.isLoading || fieldsQuery.isUninitialized ? (
								<Skeleton width='100%' height='3em' />
							) : (
								<Stack spacing={1}>
									<ContactField
										{...{
											contact: values.contacts[0],
											index: 0,
											view: VIEW_TYPES.create,
											fields,
											// expanded,
											// setExpanded: handleAccordionChange,
											disabled,
										}}
									/>
								</Stack>
							)}
						</DialogContent>
						<DialogActions>
							<Button
								onClick={() => {
									resetForm();
									onClose?.();
								}}
								color='primary'
							>
								Cancel
							</Button>

							<Button
								color='primary'
								variant='contained'
								disabled={isSubmitting}
								onClick={async () => {
									await handleSubmit();
									setTimeout(() => {
										validateForm();
									}, 100);
								}}
							>
								{isSubmitting ? (
									<CircularProgress size='2em' sx={{ color: '#ffffff' }} />
								) : hasExistingContact ? (
									'Add'
								) : (
									'Create'
								)}
							</Button>
						</DialogActions>
						<DevJSONView value={{ values, errors }} />
					</Form>
				);
			}}
		</Formik>
	);
};

export const AddContactDialog = () => {
	const dispatch = useDispatch();

	const { getFeatureFlags } = useFeatureFlagContext();
	const useNewCreateClientForm = Boolean(getFeatureFlags('createNewClient')?.active);

	const openState = useSelector((state) => state.dialog.open.contact);
	const open = openState && useNewCreateClientForm;

	const handleClose = () => {
		dispatch(closeDialog('contact'));
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			hideBackdrop={true}
			sx={{ minWidth: '100%', padding: '1em', maxWidth: 'md' }}
			fullWidth
		>
			{open && (
				<ClientSchemaProvider {...{ view: VIEW_TYPES.update, variant: 'addContact' }}>
					<AddContactFormWrapper onClose={handleClose} />
				</ClientSchemaProvider>
			)}
		</Dialog>
	);
};

const mappedContact = (contact) => ({
	FirstName: contact[FIRST_NAME],
	LastName: contact[LAST_NAME],
	isMainContact: contact[IS_MAIN_CONTACT],
	BirthDate: contact[DATE_OF_BIRTH],
	Email: contact[EMAIL],
	Telephone: contact[PHONE],
	Telephone2: contact[PHONE2],
	JobTitle: contact[ROLE],
});

const OldAddContactDialogWrapper = () => {
	const dispatch = useDispatch();

	const { clientRow } = useObjectContext();

	const initialValues = {
		contacts: [
			{
				[FIRST_NAME]: '',
				[LAST_NAME]: '',
				[PHONE]: '',
				[PHONE2]: '',
				[DATE_OF_BIRTH]: '',
				[EMAIL]: '',
				[IS_MAIN_CONTACT]: false,
				[ROLE]: '',
			},
		],
		[CONTACT_ERROR]: false,
		errorMessage: '',
	};

	const [createContact] = useCreateContactMutation();

	const handleClose = () => {
		dispatch(closeDialog('contact'));
	};

	const handleCreateContacts = async (values) => {
		const createdContacts = [];
		for (const contact of values.contacts) {
			try {
				const body = mappedContact(contact);
				const res = await createContact({
					clientId: clientRow?.clientId,
					...body,
				}).unwrap();
				console.log('Created contact res: ', res);
				createdContacts.push(res);
			} catch (error) {
				console.error('Error creating contact:', error);
				createdContacts.push(false);
			}
		}
		return createdContacts;
	};

	return (
		<Formik
			{...{
				initialValues,
				validationSchema,
				enableReinitialize: true,
				validateOnBlur: true,
				onSubmit: async (values, { resetForm, setSubmitting }) => {
					console.log('📋 ~ Formik values', values);
					try {
						const res = await handleCreateContacts(values);
						if (res) {
							console.log('🚀 ~ Successfully created contact', res);
							resetForm();
							handleClose();
						} else {
							console.log('🙅 ~ Error creating contact', res?.error);
						}
					} catch (err) {
						console.log('🙅 ~ Error creating contact', err);
					}
					setSubmitting(false);
				},
			}}
		>
			{(formik) => {
				const { isSubmitting, values, errors, handleSubmit, validateForm } = formik;
				const hasExistingContact = values.contacts?.some(
					(contact) => contact?.[EXISTING_CONTACT],
				);

				return (
					<Form>
						<DialogTitle>Add Contact</DialogTitle>
						<DialogContent sx={{ paddingBottom: '2em' }}>
							<OldContactFields
								loading={isSubmitting}
								index={0}
								hideHeader={true}
								allowNonPrimary={true}
							/>
							{values.errorMessage.length > 0 && (
								<FormHelperText error>{values.errorMessage}</FormHelperText>
							)}
						</DialogContent>
						<DialogActions>
							<Button
								onClick={() => {
									formik.resetForm();
									handleClose();
								}}
								color='primary'
							>
								Cancel
							</Button>

							<Button
								color='primary'
								type='submit'
								variant='contained'
								disabled={isSubmitting || errors.contactError}
							>
								{isSubmitting ? (
									<CircularProgress size='2em' sx={{ color: '#ffffff' }} />
								) : hasExistingContact ? (
									'Add'
								) : (
									'Create'
								)}
							</Button>
						</DialogActions>
					</Form>
				);
			}}
		</Formik>
	);
};

export const OldAddContactDialog = () => {
	const dispatch = useDispatch();

	const { getFeatureFlags, isSuccess } = useFeatureFlagContext();
	const useNewCreateClientForm = Boolean(getFeatureFlags('createNewClient')?.active);

	const openState = useSelector((state) => state.dialog.open.contact);
	const open = openState && !useNewCreateClientForm && isSuccess;

	const handleClose = () => {
		dispatch(closeDialog('contact'));
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			hideBackdrop={true}
			sx={{ minWidth: '100%', padding: '1em', maxWidth: 'lg' }}
			fullWidth
		>
			{open && <OldAddContactDialogWrapper />}
		</Dialog>
	);
};
